import {
  CollectionBuyerEngagement,
  CollectionSequence,
  CollectionTrigger,
  formatMoney,
} from '@dill/dill-shared';

// Shared by every reporting view that needs to show "is this buyer in a sequence/trigger, which
// one, and which step" (AR Aging, Invoice Collection Notes, ...) - kept in one place so the two
// don't drift on how a buyer's engagement/progress resolves into display text.

// Step titles can contain {{buyerName}}/{{amount}} tokens (filled in for real when the backend
// creates the step's task - see resolveTemplate in collectionSequenceService.ts) - mirrored here
// so reports show "Call Acme Co about $285.50 past due balance" instead of the raw template.
const resolveStepTemplate = (
  template: string | null | undefined,
  tokens: { buyerName: string; amount: number }
): string =>
  (template || '')
    .replace(/{{\s*buyerName\s*}}/g, tokens.buyerName)
    .replace(/{{\s*amount\s*}}/g, formatMoney(tokens.amount, 2));

// A buyer's current step, described as "Step X of N: <title>" - falls back to the sequence's
// own step.order (rather than a plain array index) when looking up the step, mirroring the
// backend's own lookup in collectionSequenceService.ts's advanceBuyerSequenceProgress, since
// currentStepIndex is stored against `order`, not necessarily the array position.
const describeSequenceStep = (
  sequence: Pick<CollectionSequence, 'steps'> | undefined,
  currentStepIndex: number | undefined,
  tokens: { buyerName: string; amount: number }
): string => {
  if (!sequence || currentStepIndex == null) return '';
  const orderedSteps = [...sequence.steps].sort((a, b) => a.order - b.order);
  const currentStep =
    sequence.steps.find((s) => s.order === currentStepIndex) ?? sequence.steps[currentStepIndex];
  if (!currentStep) return `Step ${currentStepIndex + 1} of ${orderedSteps.length}`;
  const position = orderedSteps.findIndex((s) => s.id === currentStep.id) + 1;
  const title = resolveStepTemplate(currentStep.title, tokens);
  return `Step ${position || currentStepIndex + 1} of ${orderedSteps.length}: ${title}`;
};

export interface BuyerCollectionEngagementInfo {
  collectionStatus: 'Sequence' | 'Trigger' | '';
  collectionName: string;
  collectionStep: string;
}

// Resolves one buyer's current engagement into display fields - a buyer is attached to at most
// one sequence or trigger at a time (the global mutual-exclusion rule enforced by
// claimBuyerEngagement on the backend), so this always resolves to a single status/name/step.
export const resolveBuyerCollectionEngagement = ({
  buyerId,
  buyerName,
  amount,
  engagementsByBuyerId,
  progressByBuyerId,
  collectionSequences,
  collectionTriggers,
}: {
  buyerId: string;
  buyerName: string;
  amount: number;
  engagementsByBuyerId: Record<string, CollectionBuyerEngagement>;
  progressByBuyerId: Record<string, { currentStepIndex: number; status: string }>;
  collectionSequences: Pick<CollectionSequence, 'id' | 'name' | 'steps'>[];
  collectionTriggers: Pick<CollectionTrigger, 'id' | 'name'>[];
}): BuyerCollectionEngagementInfo => {
  const engagement = engagementsByBuyerId[buyerId];

  if (engagement?.type === 'SEQUENCE_ENROLLED' && engagement.sequenceId) {
    const sequence = collectionSequences.find((s) => s.id === engagement.sequenceId);
    const progress = progressByBuyerId[buyerId];
    let collectionStep = describeSequenceStep(sequence, progress?.currentStepIndex, {
      buyerName,
      amount,
    });
    if (progress?.status === 'PAUSED') {
      collectionStep = collectionStep ? `${collectionStep} (Paused)` : 'Paused';
    }
    return { collectionStatus: 'Sequence', collectionName: sequence?.name || '', collectionStep };
  }

  if (engagement?.type === 'TRIGGER_TARGET' && engagement.triggerId) {
    const trigger = collectionTriggers.find((t) => t.id === engagement.triggerId);
    return { collectionStatus: 'Trigger', collectionName: trigger?.name || '', collectionStep: '' };
  }

  return { collectionStatus: '', collectionName: '', collectionStep: '' };
};
